import { createPublicClient, http, parseEventLogs, type PublicClient } from "viem";
import { getPublicClient } from "wagmi/actions";
import { config } from "../wagmi";
import { cadenas } from "../config/chains";
import type { RedKeel } from "../config/networks";

/**
 * EVENTOS PRE-INDEXADOS. Un log crudo tal y como lo devuelve `eth_getLogs`, guardado por
 * `indexer-eventos.py` en el fichero que apunta `red.indexadorEventos`. `blockNumber` viaja como
 * texto decimal porque JSON no tiene enteros grandes.
 */
type LogCrudo = {
  address: string;
  topics: string[];
  data: string;
  blockNumber: string;
  transactionHash: string;
  logIndex: number;
};
type Indexado = { lastBlock: number; updatedAt: number; vaults: Record<string, LogCrudo[]> };

/** El fichero indexado se pide UNA vez por ventana de 30 s y lo comparten todos los que lo lean:
 *  abrir una ficha dispara tres consultas de eventos del mismo vault y no queremos tres descargas. */
const cacheIndex = new Map<string, { t: number; p: Promise<Indexado | null> }>();
function leerIndexado(fichero: string): Promise<Indexado | null> {
  const previa = cacheIndex.get(fichero);
  if (previa && Date.now() - previa.t < 30_000) return previa.p;
  const p = fetch(`/${fichero}?t=${Date.now()}`)
    .then((r) => (r.ok ? (r.json() as Promise<Indexado>) : null))
    .catch(() => null);
  cacheIndex.set(fichero, { t: Date.now(), p });
  return p;
}

/** Un log crudo del índice, con los tipos que espera viem (`blockNumber` en bigint). */
const aLog = (l: LogCrudo) =>
  ({
    address: l.address as `0x${string}`,
    topics: l.topics as [`0x${string}`, ...`0x${string}`[]],
    data: l.data as `0x${string}`,
    blockNumber: BigInt(l.blockNumber),
    transactionHash: l.transactionHash as `0x${string}`,
    logIndex: l.logIndex,
    removed: false,
  }) as never;


/**
 * El cliente de lectura de una red, con un tipo que NO crece con el numero de cadenas.
 *
 * POR QUE EXISTE. `getPublicClient(config, { chainId })` devuelve un cliente cuyo tipo es la UNION
 * de todas las cadenas del config, y cada `simulateContract` o `multicall` sobre el multiplica esa
 * union por sus propias variantes. Con tres cadenas aguantaba; al entrar BNB Chain el 2026-08-18,
 * TypeScript se rindio con `TS2590: expression produces a union type that is too complex to
 * represent` en cuatro hooks a la vez — y no era un fallo de esos hooks, que no se habian tocado.
 *
 * El `as never` del chainId ya estaba puesto a mano en `useVaults.ts` por esta misma razon: era el
 * mismo problema asomando antes. Aqui se resuelve en UN sitio, con el motivo escrito, en vez de
 * repartir casts por los hooks cada vez que se añada una red.
 *
 * Lo que se pierde con el tipo ancho es la inferencia de la cadena concreta, que aqui no aporta
 * nada: la direccion y el ABI ya los pone quien llama, y la red se elige en tiempo de ejecucion.
 */
export function clientePublico(idCadena: number): PublicClient {
  return getPublicClient(config, { chainId: idCadena as never }) as unknown as PublicClient;
}

/**
 * El cliente para PEDIR EVENTOS, que no es el mismo que para leer estado.
 *
 * `rpcLogs` existe en `networks.ts` desde hace tiempo justamente para esto —hay RPC que sirven
 * `eth_call` de maravilla y rechazan `eth_getLogs`— pero hasta el 2026-08-18 solo lo usaba
 * `useGobierno`. Los otros cinco sitios que piden eventos (el «lleva trabajando», el historial, el
 * gasto de gas, la actividad y la flota) iban por el RPC normal, y por eso en Arbitrum y HyperEVM
 * decian «no encuentro la apertura» sin que nada estuviera roto: el endpoint simplemente no
 * contesta a esa pregunta.
 */
export function clienteLogs(red: RedKeel): PublicClient {
  if (!red.rpcLogs) return clientePublico(red.idCadena);
  const cadena = cadenas.find((c) => c.id === red.idCadena);
  return createPublicClient({
    chain: cadena,
    // 25 s, no los 10 de fabrica. Una consulta de eventos en BNB Chain tarda ~5 s de forma normal y
    // a veces mas; con el timeout corto, la ventana se daba por perdida antes de contestar y la
    // pantalla decia «no encuentro la apertura» por impaciencia, no por falta de datos.
    transport: http(red.rpcLogs, { timeout: 25_000 }),
  }) as PublicClient;
}

/**
 * Busca eventos aunque el RPC no deje mirar toda la cadena de una vez.
 *
 * PRIMERO SE INTENTA ENTERO, que es una sola ida y vuelta y es lo que funciona en tres de las
 * cuatro redes. Si el proveedor se queja del rango —BNB Chain no acepta mas de 9.000 bloques por
 * consulta, y su dataseed no acepta ninguno— se va hacia atras por ventanas desde el bloque de
 * ahora, y se PARA en la primera que traiga algo.
 *
 * Hacia atras y no hacia delante porque lo que se busca es lo ULTIMO que paso: la apertura vigente,
 * no la primera de la historia del vault. Asi el caso normal —una posicion abierta hace poco— se
 * resuelve en una o dos consultas en vez de recorrer la cadena entera.
 *
 * Y no se baja de `bloqueFactory`: ningun vault es anterior a la factory que lo creo, asi que por
 * debajo no hay nada que encontrar. Sin ese suelo, una red troceada se comeria cientos de
 * consultas recorriendo cadena vacia.
 *
 * `ventanas` acota el esfuerzo por si acaso. Si no aparece ahi, se devuelve vacio y la pantalla lo
 * dice, que es mejor que treinta peticiones y una rueda girando.
 */
/**
 * TODOS los eventos de un filtro, o NADA. Es la hermana de `logsConVentanas` para cuando lo que se
 * busca no es el último evento sino la SUMA de todos: el total ganado por un vault desde que abrió.
 *
 * Por eso no puede parar en la primera ventana con datos —eso daría una suma menor que la de verdad,
 * y una cifra de dinero incompleta presentada como completa es peor que no tener cifra— y por eso
 * devuelve `null` en vez de una lista a medias cuando no consigue recorrerlo entero.
 *
 * En Arbitrum, Robinhood e HyperEVM se resuelve en UNA consulta desde el bloque 0. BNB Chain no
 * acepta rangos grandes (ver `networks.ts`), así que ahí se trocea desde el bloque de la factory; si
 * el troceo no llega hasta arriba dentro del tope, se devuelve `null`.
 *
 * El tope existe para que una red que crezca no convierta la pantalla en cientos de peticiones. El
 * día que BNB Chain no quepa, la salida no es subir el número: es indexar los eventos fuera.
 */
/**
 * Ensambla el histórico de un vault (o de varios) desde el índice del VPS + la punta on-chain.
 * `null` cuando el índice no está, se quedó demasiado atrás, o no se pudo leer la punta: quien llama
 * cae entonces al escaneo on-chain completo.
 */
async function logsDesdeIndice<T>(
  filtro: { address: `0x${string}` | `0x${string}`[]; event?: unknown },
  red: RedKeel
): Promise<T[] | null> {
  const idx = await leerIndexado(red.indexadorEventos!);
  if (!idx) return null;

  // NADA DE CADENA EN EL CAMINO RÁPIDO. Un `eth_getLogs` en BNB cuesta ~5 s aunque pida 20 bloques,
  // y hacerlo en cada lectura de eventos era justo lo que hacía la ficha «tardar demasiado». El
  // índice ya trae TODO el histórico; se lee y punto. La frescura la da el indexer, que reindexa
  // cada pocos segundos, no una consulta on-chain por página.
  //
  // Se comprueba por `updatedAt` (no contra el bloque de ahora, que sería otra llamada RPC): si el
  // indexer lleva parado un rato, el índice se queda corto y se cae al escaneo on-chain de siempre.
  if (Date.now() / 1000 - idx.updatedAt > 300) return null;

  const dirs = (Array.isArray(filtro.address) ? filtro.address : [filtro.address]).map((a) =>
    a.toLowerCase()
  );
  const base = dirs.flatMap((d) => idx.vaults[d] ?? []).map(aLog);

  // Con `event` se devuelve DECODIFICADO (con `.args`), como haría `getLogs` filtrando; sin él, crudo.
  return (filtro.event
    ? parseEventLogs({ abi: [filtro.event] as never, logs: base as never })
    : base) as T[];
}

export async function logsHistoricos<T>(
  client: PublicClient,
  /** Sin `event` pide TODO lo que emite esa dirección, que es una consulta en vez de una por evento. */
  filtro: { address: `0x${string}` | `0x${string}`[]; event?: unknown },
  red: RedKeel,
  opciones?: { ventanas?: number }
): Promise<T[] | null> {
  const f = filtro as never;

  // 0) EVENTOS PRE-INDEXADOS. Si la red los sirve y el índice está al día, se lee de ahí el grueso del
  //    histórico y solo se escanea on-chain el tramo posterior —barato—, en vez de recorrer la cadena
  //    entera por ventanas (que en BNB Chain ya no cabe en el tope). Si falta o se quedó atrás, `null`
  //    y se sigue al escaneo on-chain de siempre: es una optimización, nunca una dependencia.
  if (red.indexadorEventos) {
    const indexado = await logsDesdeIndice<T>(filtro, red);
    if (indexado !== null) return indexado;
  }

  const trocea = !!(red.ventanaLogs && red.bloqueFactory);
  if (!trocea) {
    // Redes que aceptan la cadena entera de una vez (Arbitrum, Robinhood): una sola consulta desde 0.
    try {
      return (await client.getLogs({ ...(f as object), fromBlock: 0n, toBlock: "latest" } as never)) as T[];
    } catch {
      return null; // sin config de troceo no hay forma de recorrerla: mejor null que una suma corta
    }
  }
  // Redes con troceo configurado (BNB Chain, HyperEVM): el RPC RECHAZA el rango completo SIEMPRE, así
  // que ni se intenta —era una ida y vuelta perdida (y en HyperEVM podía comerse el timeout) en cada
  // consulta— y se va directo por ventanas.
  const tamano = BigInt(red.ventanaLogs!); // `trocea` garantiza los dos; TS no lo infiere del bool
  // El tope solo eleva el TECHO de ventanas: se construyen solo las que hagan falta para llegar a la
  // cabeza (bucle de abajo), asi que subirlo no anade trabajo en cadenas cortas. 200 da a Base (que
  // trocea a 9.900 por el cap de 10k de base.org y no tiene indexer) ~2M bloques de recorrido ≈ 45
  // dias de margen segun crece la cadena desde la factory; el dia que no quepa, la salida es indexar.
  const tope = opciones?.ventanas ?? 200;
  let desde = BigInt(red.bloqueFactory!);
  let alto: bigint;
  try {
    alto = await client.getBlockNumber();
  } catch {
    return null;
  }

  // Las ventanas van EN LOTES, no de una en una. Son independientes entre sí —cada una es un tramo
  // distinto de la cadena— y en serie esto tardaba medio minuto en BNB Chain, la única red que hay
  // que trocear. De DOCE en doce: bloXroute aguanta esa docena seguida sin limitar (probado el
  // 2026-08-18 desde el VPS) y cada usuario va con su propia IP, así que el límite no se comparte.
  const tramos: [bigint, bigint][] = [];
  for (let i = 0; i < tope && desde <= alto; i++) {
    const hasta = desde + tamano > alto ? alto : desde + tamano;
    tramos.push([desde, hasta]);
    desde = hasta + 1n;
  }
  if (desde <= alto) return null; // se acabó el tope antes que la cadena: la suma estaría corta

  const todos: T[] = [];
  for (let i = 0; i < tramos.length; i += 12) {
    const lote = await Promise.all(
      tramos.slice(i, i + 12).map(([a, b]) =>
        client
          .getLogs({ ...(f as object), fromBlock: a, toBlock: b } as never)
          .then((r) => r as T[])
          .catch(() => null)
      )
    );
    if (lote.some((x) => x === null)) return null; // un hueco en medio ya invalida la suma
    for (const x of lote) todos.push(...(x as T[]));
  }
  return todos;
}

export async function logsConVentanas<T>(
  client: PublicClient,
  filtro: { address: `0x${string}` | `0x${string}`[]; event: unknown },
  red?: RedKeel,
  /** `acumula`: NO parar en la primera ventana con datos, seguir hasta tener un evento por cada
   *   dirección del filtro. Sin él, un filtro con VARIAS direcciones que abrieron en bloques
   *   distintos solo devuelve las de la ventana más reciente y las demás se quedan sin fecha. */
  opciones?: { ventanas?: number; acumula?: boolean }
): Promise<T[]> {
  const tamano = BigInt(red?.ventanaLogs ?? 9_000);
  const suelo = BigInt(red?.bloqueFactory ?? 0);
  const ventanas = opciones?.ventanas ?? 20;
  const f = filtro as never;

  // 0) ÍNDICE DE EVENTOS (BNB). El `Opened` que buscan la portada (`useFlota`) y la ficha
  //    (`useApertura`) ya está indexado. Sin esto, esta función escanea hasta 20 ventanas hacia atrás
  //    —~2 min en BNB Chain— y era justo lo que dejaba la piscina sin salir en la portada (bloqueaba
  //    el `await` del paso 6) y la ficha tardando una eternidad. Con el índice es instantáneo; si
  //    falta o está viejo, `null` y se sigue al escaneo por ventanas de siempre.
  if (red?.indexadorEventos) {
    const desdeIndice = await logsDesdeIndice<T>(filtro, red);
    if (desdeIndice !== null) return desdeIndice;
  }

  const trocea = !!(red?.ventanaLogs && red?.bloqueFactory);

  // Solo se intenta la cadena entera donde el RPC la acepta. En BNB Chain y HyperEVM (las que traen
  // `ventanaLogs`) el rango completo se rechaza SIEMPRE: intentarlo era una ida y vuelta perdida en
  // cada consulta de eventos, así que se va directo por ventanas hacia atrás.
  if (!trocea) {
    try {
      return (await client.getLogs({ ...(f as object), fromBlock: 0n, toBlock: "latest" } as never)) as T[];
    } catch {
      // El RPC no traga el rango completo: se va por ventanas.
    }
  }

  let alto: bigint;
  try {
    alto = await client.getBlockNumber();
  } catch {
    return [];
  }

  // Con `acumula`, se junta lo de todas las ventanas y se para en cuanto cada dirección tiene ya al
  // menos un evento (se escanea de lo más nuevo a lo más viejo, así el primero que aparece de cada
  // una es el más reciente). Sin `acumula`, se devuelve la primera ventana con datos, como siempre.
  const acumulado: T[] = [];
  const quiero = new Set(
    (Array.isArray(filtro.address) ? filtro.address : [filtro.address]).map((a) => a.toLowerCase())
  );
  const visto = new Set<string>();
  for (let i = 0; i < ventanas && alto > suelo; i++) {
    const bajo = alto > suelo + tamano ? alto - tamano : suelo;
    try {
      const trozo = (await client.getLogs({ ...(f as object), fromBlock: bajo, toBlock: alto } as never)) as T[];
      if (trozo.length) {
        if (!opciones?.acumula) return trozo;
        acumulado.push(...trozo);
        for (const l of trozo) {
          const a = (l as { address?: string }).address?.toLowerCase();
          if (a) visto.add(a);
        }
        if (visto.size >= quiero.size) return acumulado; // ya hay evento para cada dirección
      }
    } catch {
      // Una ventana que falla no invalida la busqueda: se sigue hacia atras.
    }
    if (bajo <= suelo) break;
    alto = bajo - 1n;
  }
  return acumulado;
}
